import {
    buildExportFilename,
    downloadItemsAsCSV,
    downloadTimeSeriesAsCSV,
    getHierarchyCSVColumns,
} from 'utils/formatCSV';
import { DATA_SOURCES, TIKTOK_COUNTRIES, isCovered } from '@/config/constants';
import { createDefaultColumns } from 'components/DataVis/DataTable/utils';
import { downloadTypes } from 'components/Button/DownloadCSVMenuButton';
import { getHierarchy } from 'utils/hooks/useGetHierarchy';

/**
 * Google's no-data copy, in the shape the shared Explore table's
 * `noDataMessageOf` prop calls.
 *
 * `volume` is tested for presence, not truthiness: a volume of zero is a real
 * answer and must not read as missing coverage.
 */
export const googleNoDataMessage = ({
    rootCategoryId,
    trendsData,
    categoryData,
    type,
}) => {
    if (!rootCategoryId) return 'Choose an Industry';
    if (trendsData && trendsData.length > 0)
        return `No ${type} available. Try adjusting the filters.`;
    if (!(
        categoryData &&
        Object.prototype.hasOwnProperty.call(categoryData, 'volume')
    ))
        return `No ${
            type === 'trends' ? 'subcategories' : type
        } to display. Coverage may not be available for this region.`;
    return `No ${type} available.`;
};

/**
 * The full-screen states that replace the Google Explore table rather than
 * being rendered inside it. Coverage is per market and cat1, so an uncovered
 * pair has to say so - the table would otherwise render a row per entity with
 * every metric empty.
 */
export const googleExploreBlocker = ({
    rootCategoryId,
    parentCat,
    country,
}) => {
    if (!rootCategoryId) return 'industries';
    if (
        parentCat &&
        country &&
        !isCovered({
            geo: country?.text_id,
            dataSource: DATA_SOURCES.GOOGLE,
            cat1: parentCat?.hierarchies?.[0]?.[1]?.cat1,
        })
    )
        return 'coverage';
    return null;
};

/** Google's rows are 65 high; the shared table's default derives 75. */
export const GOOGLE_ROW_HEIGHT = 65;

/**
 * Google's Explore download menu, in the shape the shared Explore table's
 * `getDownloadOptions` prop calls.
 */
export function getGoogleDownloadOptions({
    entity,
    country,
    date,
    sortedData,
    visibleOrderedMetrics,
    paginateBy,
    isAdmin,
}) {
    const trendDimension = paginateBy;
    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);
    const visibleColumnsConfig = createDefaultColumns(
        ['name', ...visibleOrderedMetrics],
        { showHealthStatLabels: true },
    );
    const baseParams = {
        entity,
        country,
        source: 'google',
        type: trendDimension === 'brands' ? 'brand' : 'trend',
        date,
    };
    return [
        {
            mixpanelMeta: { label: 'card data csv' },
            children: 'Card Data CSV',
            onClick: () => {
                const csvData = sortedData.map((item) => {
                    const { parent, hierarchy_hint } = getHierarchy(
                        item.hierarchies,
                    );
                    return hierarchy_hint
                        ? { ...item, hierarchies: [[parent, hierarchy_hint]] }
                        : item;
                });
                downloadItemsAsCSV(
                    buildExportFilename({ ...baseParams, kind: 'data' }),
                    csvData,
                    [
                        ...visibleColumnsConfig.map(
                            ({ key, label, accessor }) => ({
                                key,
                                label,
                                accessor,
                            }),
                        ),
                        { key: 'cluster' },
                        { key: 'healthStats' },
                        ...(trendDimension === 'categories'
                            ? getHierarchyCSVColumns(isAdmin)
                            : []),
                    ],
                );
            },
            downloadType: downloadTypes.CSV,
            key: 'card data csv',
        },
        {
            mixpanelMeta: { label: 'time series csv' },
            children: 'Time Series CSV',
            onClick: () =>
                downloadTimeSeriesAsCSV(
                    buildExportFilename({ ...baseParams, kind: 'time-series' }),
                    sortedData,
                    { skipPrediction: !isNotTikTokMarket },
                ),
            downloadType: downloadTypes.CSV,
            key: 'time series csv',
        },
    ];
}
